import { Button } from "@/components/ui/button";
import { SelectSearch } from "@/components/select-search";
import { PaymentSection } from "@/features/dash/registrations/components/payment-section";
import { PeriodSelector } from "@/features/dash/registrations/components/period-selector";
import { PriceSection } from "@/features/dash/registrations/components/price-section";
import { PromotionSelector } from "@/features/dash/registrations/components/promotion-selector";
import { AdultForm } from "@/features/dash/registrations/forms/adult-form";
import { ChildForm } from "@/features/dash/registrations/forms/child-form";
import { api } from "@/lib/api";
import { registrationFormSchema, type RegistrationFormData } from "@/schemas/registration-schema";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, Loader2, Plus, Trash2 } from "lucide-react";
import type { AdultBase, AgeGroupBase, ChildBase, PaymentAmounts } from "nautika-types";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { getHttpStatusAndMessage } from "@/lib/network-utils";

// Helper pour formater les noms avec "et" à la française
function formatChildNamesWithEt(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} et ${names[1]}`;
  const allButLast = names.slice(0, -1).join(", ");
  return `${allButLast} et ${names[names.length - 1]}`;
}

// Utilitaire age
function computeAge(birthDate: string | Date): number {
  const birth = new Date(birthDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

export function NewRegistration() {
  // Etat principal
  const [selectedAdults, setSelectedAdults] = useState<AdultBase[]>([]);
  const [selectedChildren, setSelectedChildren] = useState<ChildBase[]>([]);
  const [selectedPeriods, setSelectedPeriods] = useState<{ [childId: string]: string[] }>({});
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | null>(null);
  const [subtotalAmount, setSubtotalAmount] = useState<number>(0);
  const [paymentDetails, setPaymentDetails] = useState<PaymentAmounts>({ card: 0, ancv: 0, cash: 0, check: 0 });
  const [openAdultSheet, setOpenAdultSheet] = useState(false);
  const [openChildSheet, setOpenChildSheet] = useState(false);
  const [editingAdult, setEditingAdult] = useState<AdultBase | undefined>();
  const [editingChild, setEditingChild] = useState<ChildBase | undefined>();

  // Recherches locales pour filtrage manuel (garantit visibilité bouton création)
  const [adultSearch, setAdultSearch] = useState("");
  const [childSearch, setChildSearch] = useState("");

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Data fetching
  const { data: adultsData, isLoading: loadingAdults } = api.useQuery("get", "/registrations/adults/", { staleTime: 60000 });
  const { data: childrenData, isLoading: loadingChildren } = api.useQuery("get", "/registrations/children/", { staleTime: 60000 });
  const { data: groupsData } = api.useQuery("get", "/club/age-groups", { staleTime: 60000 });
  const { data: promotionsData } = api.useQuery("get", "/promotions/", { staleTime: 60000 });

  const adultList = useMemo(() => (adultsData || []) as AdultBase[], [adultsData]);
  const childList = useMemo(() => (childrenData || []) as ChildBase[], [childrenData]);

  // Détection de fiche dupliquée (ensemble exact d'enfants sélectionnés)
  const selectedChildIdsCsv = useMemo(
    () =>
      selectedChildren
        .map((c: ChildBase) => c.id)
        .sort()
        .join(","),
    [selectedChildren]
  );
  const { data: duplicatesData, isLoading: loadingDuplicates } = api.useQuery("get", "/registrations/duplicates", {
    params: { query: { childIds: selectedChildIdsCsv || undefined } },
    staleTime: 60_000,
    enabled: selectedChildren.length > 0,
  });
  const duplicateRegistration = (duplicatesData && (duplicatesData as Array<{ id: string }>)[0]) || null;
  const hasDuplicateChildrenSet = !!duplicateRegistration;
  const promotionList = (promotionsData || []) as { id: string; type: string; value: number }[];

  // Maps groupes
  const groupMap = useMemo(() => {
    const list = (groupsData || []) as AgeGroupBase[];
    const map: Record<string, string> = {};
    list.forEach((g) => {
      map[g.id] = g.name;
    });
    return map;
  }, [groupsData]);

  // Filtrage manuel (case insensitive) pour garantir que l'item de création reste affiché
  const filteredAdults = useMemo(() => {
    const term = adultSearch.trim().toLowerCase();
    if (!term) return adultList;
    return adultList.filter((a) => `${a.firstName} ${a.lastName} ${a.phone || ""}`.toLowerCase().includes(term));
  }, [adultSearch, adultList]);

  const filteredChildren = useMemo(() => {
    const term = childSearch.trim().toLowerCase();
    if (!term) return childList;
    return childList.filter((c) => `${c.firstName} ${c.lastName} ${c.groupId ? groupMap[c.groupId] : ""}`.toLowerCase().includes(term));
  }, [childSearch, childList, groupMap]);

  const selectedPromotion = promotionList.find((p) => p.id === selectedPromotionId);

  // Calcul réduction promotion
  const promotionDiscount = useMemo(() => {
    if (!selectedPromotion || subtotalAmount === 0) return 0;
    return selectedPromotion.type === "percentage" ? (subtotalAmount * selectedPromotion.value) / 100 : selectedPromotion.value / 100;
  }, [selectedPromotion, subtotalAmount]);

  // Mutation pour créer une nouvelle inscription
  const createRegistrationMutation = api.useMutation("post", "/registrations/", {
    onSuccess: () => {
      toast.success("Inscription créée avec succès !");
      // Invalider le cache des inscriptions
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/"] });
      // Rediriger vers la liste des inscriptions
      navigate("/dash/registrations");
    },
    onError: (error) => {
      console.error("Error creating registration:", error);
      const { status, message } = getHttpStatusAndMessage(error);
      if (status === 409 || message?.toLowerCase?.().includes("already reserved")) {
        toast.error("Conflit de réservation", {
          description: "Un enfant est déjà réservé pour une des périodes sélectionnées.",
        });
      } else {
        toast.error("Erreur lors de la création de l'inscription");
      }
    },
  });

  const handleAdultCreated = useCallback(
    (adult: AdultBase) => {
      setSelectedAdults((prev) => {
        const exists = prev.find((a) => a.id === adult.id);
        return exists ? prev.map((a) => (a.id === adult.id ? adult : a)) : [...prev, adult];
      });
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/adults/"] });
      setEditingAdult(undefined);
    },
    [queryClient]
  );

  const handleChildCreated = useCallback(
    (child: ChildBase) => {
      setSelectedChildren((prev) => {
        const exists = prev.find((c) => c.id === child.id);
        return exists ? prev.map((c) => (c.id === child.id ? child : c)) : [...prev, child];
      });
      queryClient.invalidateQueries({ queryKey: ["get", "/registrations/children/"] });
      setEditingChild(undefined);
    },
    [queryClient]
  );

  const handleEditAdult = (adult: AdultBase) => {
    setEditingAdult(adult);
    setOpenAdultSheet(true);
  };

  const handleEditChild = (child: ChildBase) => {
    setEditingChild(child);
    setOpenChildSheet(true);
  };

  const handleNewAdult = () => {
    setEditingAdult(undefined);
    setOpenAdultSheet(true);
  };

  const handleNewChild = () => {
    setEditingChild(undefined);
    setOpenChildSheet(true);
  };

  // Calculer si les sections peuvent être affichées
  const hasChildren = selectedChildren.length > 0;
  const hasSelectedPeriods = Object.values(selectedPeriods).some((periods) => periods.length > 0);

  // Calculer les enfants sans périodes
  const childrenWithoutPeriods = useMemo(
    () => selectedChildren.filter((child) => !selectedPeriods[child.id] || selectedPeriods[child.id].length === 0),
    [selectedChildren, selectedPeriods]
  );

  // Validation du formulaire avec Zod
  const validationResult = useMemo(() => {
    const formData: RegistrationFormData = {
      adults: selectedAdults.map((adult) => ({ id: adult.id })),
      children: selectedChildren.map((child) => ({ id: child.id })),
      periods: selectedPeriods,
      promotionId: selectedPromotionId || undefined,
      price: subtotalAmount,
      paymentMethods: paymentDetails,
    };
    return registrationFormSchema.safeParse(formData);
  }, [selectedAdults, selectedChildren, selectedPeriods, selectedPromotionId, subtotalAmount, paymentDetails]);

  const isFormValid = validationResult.success;
  const formErrors = useMemo(() => (!validationResult.success ? validationResult.error.issues : []), [validationResult.success, validationResult.error]);

  // Prioritize errors: adults > children/duplicate > periods > price
  const prioritizedError = useMemo(() => {
    // 1. Check for adults error
    const adultsError = formErrors.find((e) => e.path.includes("adults"));
    if (adultsError) return adultsError.message;

    // 2. Check for children errors or duplicate registration
    const childrenError = formErrors.find((e) => e.path.includes("children"));
    if (childrenError) return childrenError.message;
    if (hasDuplicateChildrenSet) return null; // Duplicate is shown near children list, not in bottom section

    // 3. Check for periods error
    const periodsError = formErrors.find((e) => e.message.toLowerCase().includes("période"));
    if (periodsError) return periodsError.message;

    // 4. Check for price error
    const priceError = formErrors.find((e) => e.path.includes("price"));
    if (priceError) return priceError.message;

    return null;
  }, [formErrors, hasDuplicateChildrenSet]);

  const duplicateChildNamesList = useMemo(
    () => selectedChildren.map((c) => `${c.firstName || ""} ${c.lastName || ""}`.trim()).filter(Boolean),
    [selectedChildren]
  );
  const duplicateChildNames = useMemo(() => formatChildNamesWithEt(duplicateChildNamesList), [duplicateChildNamesList]);

  // Fonction pour soumettre le formulaire
  const handleSubmit = () => {
    if (!isFormValid) {
      toast.error("Des informations sont manquantes :", { description: formErrors.map((error) => error.message).join(", ") });
      return;
    }

    // Préparer les données pour l'API
    const childrenReservations = selectedChildren
      .map((child) => ({
        childId: child.id,
        periodIds: selectedPeriods[child.id] || [],
      }))
      .filter((reservation) => reservation.periodIds.length > 0);

    const registrationData = {
      adultIds: selectedAdults.map((adult) => adult.id),
      childrenReservations,
      paymentDetails,
      promotionId: selectedPromotionId || undefined,
      subtotalAmount,
      promotionDiscount,
    };

    createRegistrationMutation.mutate({ body: registrationData });
  };

  // Callbacks stabilisés pour éviter les re-renders
  const handlePeriodsChange = useCallback((periods: { [childId: string]: string[] }) => {
    setSelectedPeriods(periods);
  }, []);

  const handlePaymentChange = useCallback((payments: PaymentAmounts) => {
    setPaymentDetails(payments);
  }, []);

  const handleSubtotalChange = useCallback((subtotal: number) => {
    setSubtotalAmount(subtotal);
  }, []);

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        {/* Titre de la section */}
        <div>
          <h2 className="text-2xl font-bold">Nouvelle inscription</h2>
        </div>
        {/* Adults field - toujours affiché */}
        <div className="flex flex-col">
          <p className="mb-2 text-lg font-semibold">Adultes de confiance</p>
          <p className="text-sm text-gray-700">
            Indiquez ici la ou les personnes susceptibles de venir chercher les enfants au club et que vous pourrez contacter en cas de nécessité
          </p>

          {/* SelectSearch pour les adultes */}
          <SelectSearch
            items={filteredAdults}
            selectedItems={selectedAdults}
            isLoading={loadingAdults}
            onItemSelect={(adult) => {
              const typedAdult = adult as AdultBase;
              const isSelected = selectedAdults.find((a) => a.id === typedAdult.id);
              if (isSelected) {
                setSelectedAdults((prev) => prev.filter((a) => a.id !== typedAdult.id));
              } else {
                setSelectedAdults((prev) => [...prev, typedAdult]);
              }
            }}
            onCreateNew={handleNewAdult}
            filterFunction={(items, searchTerm) => {
              const term = searchTerm.trim().toLowerCase();
              if (!term) return items;
              return items.filter((a: AdultBase) => `${a.firstName} ${a.lastName} ${a.phone || ""}`.toLowerCase().includes(term));
            }}
            renderItem={(adult) => {
              const typedAdult = adult as AdultBase;
              return (
                <span>
                  {typedAdult.firstName || "N/A"} {typedAdult.lastName || "N/A"}
                  {typedAdult.phone ? ` (${typedAdult.phone})` : ""}
                </span>
              );
            }}
            renderTrigger={() => (
              <Button variant="outline" className="mt-4 w-[280px] justify-start">
                <Plus className="mr-2" /> Ajouter un adulte de confiance
              </Button>
            )}
            onSearch={setAdultSearch}
            placeholder="Rechercher un adulte..."
            createNewText="Créer un nouvel adulte"
            emptyMessage="Aucun adulte trouvé"
            loadingMessage="Chargement..."
            width="w-[280px]"
            popoverAlign="start"
            popoverSide="bottom"
            showCreateButton={true}
            closeOnSelect={false}
            isMultiSelect={true}
          />
          <div className="mt-2 flex flex-col gap-y-2">
            {selectedAdults.map((adult) => (
              <div key={`selected-adult-${adult.id}`} className="flex items-center justify-between rounded-md border bg-background p-3">
                <span>
                  {adult.firstName || "N/A"} {adult.lastName || "N/A"}
                  {adult.phone ? ` (${adult.phone})` : ""}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="default" onClick={() => handleEditAdult(adult)} title="Voir la fiche">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="ml-1 text-sm text-blue-500">Voir la fiche</span>
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setSelectedAdults((prev) => prev.filter((a) => a.id !== adult.id))}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Children field - toujours affiché */}
        <div>
          <p className="mb-2 text-lg font-semibold">Enfants</p>
          <p className="text-sm text-gray-700">Renseignez ici les enfants concernés par l'inscription</p>
          {/* SelectSearch pour les enfants */}
          <SelectSearch
            items={filteredChildren}
            selectedItems={selectedChildren}
            isLoading={loadingChildren}
            onItemSelect={(child) => {
              const typedChild = child as ChildBase;
              const isSelected = selectedChildren.find((c) => c.id === typedChild.id);
              if (isSelected) {
                setSelectedChildren((prev) => prev.filter((c) => c.id !== typedChild.id));
              } else {
                setSelectedChildren((prev) => [...prev, typedChild]);
              }
            }}
            onCreateNew={handleNewChild}
            filterFunction={(items, searchTerm) => {
              const term = searchTerm.trim().toLowerCase();
              if (!term) return items;
              return items.filter((c: ChildBase) =>
                `${c.firstName} ${c.lastName} ${c.groupId && groupMap[c.groupId] ? groupMap[c.groupId] : ""}`.toLowerCase().includes(term)
              );
            }}
            renderItem={(child) => {
              const typedChild = child as ChildBase;
              return (
                <span>
                  {typedChild.firstName || "N/A"} {typedChild.lastName || "N/A"} · {typedChild.birthDate ? computeAge(typedChild.birthDate) : "N/A"} ans
                  {typedChild.groupId && groupMap[typedChild.groupId] ? <> · {groupMap[typedChild.groupId]}</> : null}
                </span>
              );
            }}
            renderTrigger={() => (
              <Button variant="outline" className="mt-4 w-[280px] justify-start">
                <Plus className="mr-2" /> Ajouter un enfant
              </Button>
            )}
            onSearch={setChildSearch}
            placeholder="Rechercher un enfant..."
            createNewText="Créer un nouvel enfant"
            emptyMessage="Aucun enfant trouvé"
            loadingMessage="Chargement..."
            width="w-[280px]"
            popoverAlign="start"
            popoverSide="bottom"
            showCreateButton={true}
            closeOnSelect={false}
            isMultiSelect={true}
          />
          <div className={`mt-2 flex flex-col gap-y-2`}>
            {selectedChildren.map((child) => (
              <div
                key={`selected-child-${child.id}`}
                className={`flex items-center justify-between rounded-md border p-3 ${hasDuplicateChildrenSet ? "rounded-md border border-red-300 bg-red-50" : "bg-background"}`}
              >
                <span>
                  {child.firstName || "N/A"} {child.lastName || "N/A"} · {child.birthDate ? computeAge(child.birthDate) : "N/A"} ans
                  {child.groupId && groupMap[child.groupId] ? <> · {groupMap[child.groupId]}</> : null}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="default" onClick={() => handleEditChild(child)} title="Voir la fiche">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="ml-1 text-sm text-blue-500">Voir la fiche</span>
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setSelectedChildren((prev) => prev.filter((c) => c.id !== child.id))}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {hasDuplicateChildrenSet && (
            <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium">
                  {duplicateChildNames ? (
                    <>
                      <em>{duplicateChildNames}</em>
                      {selectedChildren.length > 1 ? " sont déjà inscrits" : " est déjà inscrit"} sur une autre fiche.
                    </>
                  ) : selectedChildren.length > 1 ? (
                    "Une inscription existe déjà pour exactement ces enfants."
                  ) : (
                    "Une inscription existe déjà pour cet enfant."
                  )}
                </p>
                {duplicateRegistration && (
                  <a
                    className="flex items-center gap-2 whitespace-nowrap underline"
                    href={`/dash/registrations/${(duplicateRegistration as { id: string }).id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir la fiche
                    <ExternalLink className="size-4" />
                  </a>
                )}
              </div>
              {loadingDuplicates && <p className="mt-1 text-[11px] italic">Vérification en cours...</p>}
            </div>
          )}
        </div>{" "}
        {/* Period selection - toujours affiché */}
        <PeriodSelector selectedChildren={selectedChildren} onPeriodsChange={handlePeriodsChange} />
        {/* Message d'avertissement si des enfants n'ont pas de périodes */}
        {hasChildren && childrenWithoutPeriods.length > 0 && (
          <div className="rounded-md border border-dashed border-yellow-200 bg-yellow-50 p-4">
            <p className="mb-2 font-semibold text-yellow-800">Périodes manquantes</p>
            <ul className="space-y-1 text-sm text-yellow-700">
              {childrenWithoutPeriods.map((child) => (
                <li key={child.id}>
                  Aucune période n'a été sélectionnée pour {child.firstName} {child.lastName}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Prix de l'inscription - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && <PriceSection value={subtotalAmount} onPriceChange={handleSubtotalChange} />}
        {/* Promotion selection - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <PromotionSelector selectedPromotionId={selectedPromotionId} onPromotionChange={setSelectedPromotionId} />
        )}
        {/* Payment section - affiché seulement si des périodes sont sélectionnées */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <PaymentSection
            initialPayments={paymentDetails}
            promotionDiscount={promotionDiscount}
            onPaymentChange={handlePaymentChange}
            hideSubtotalColumn
            subtotalValue={subtotalAmount}
          />
        )}
        {/* Submit section - affiché seulement si le formulaire peut être soumis */}
        {hasSelectedPeriods && childrenWithoutPeriods.length === 0 && (
          <div className="w-full">
            {/* Liste des erreurs de validation - affiche uniquement l'erreur la plus importante */}
            {prioritizedError && (
              <div className="mb-4 w-full rounded-md border border-dashed border-red-200 bg-red-50 p-4 text-red-700">
                <p>{prioritizedError}</p>
              </div>
            )}
            {/* Bouton de soumission aligné à droite */}
            <div className="mt-8 flex justify-end">
              <Button onClick={handleSubmit} disabled={createRegistrationMutation.isPending || hasDuplicateChildrenSet} className="mb-48 min-w-64">
                {createRegistrationMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Création...
                  </>
                ) : hasDuplicateChildrenSet ? (
                  "Fiche dupliquée détectée"
                ) : (
                  "Ajouter cette inscription"
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Adult and Child creation forms */}
        <AdultForm open={openAdultSheet} onOpenChange={setOpenAdultSheet} onAdultCreated={handleAdultCreated} editingAdult={editingAdult} />
        <ChildForm open={openChildSheet} onOpenChange={setOpenChildSheet} onChildCreated={handleChildCreated} editingChild={editingChild} />
      </div>
    </>
  );
}
